import { gsap } from 'gsap';
import { Container, Graphics, Rectangle, type FederatedPointerEvent } from 'pixi.js';
import { BATTLE_PACE_SCALE } from '../data/constants';

// Isometric city placeholder. The player marker is pinned to the center of
// the view; tapping a tile walks the player there by moving the MAP under
// the marker (camera-follow). Real city art / character sprite replace the
// placeholder visuals later - the input plumbing, tile picking and the
// camera stay.
const GRID_SIZE = 100;
// Tile size as a fraction of the content height. Width is 2x height - the
// classic 2:1 isometric diamond.
const TILE_H_FRAC = 0.07;
// Walk speed, in tiles per second (straight-line tile distance).
const WALK_TILES_PER_SEC = 3;
// Checkerboard tile fills + grid line color.
const TILE_COLOR_A = 0x8fae72;
const TILE_COLOR_B = 0x9dbd80;
const TILE_LINE_COLOR = 0x6d8757;
const HIGHLIGHT_COLOR = 0xffd966;
const HIGHLIGHT_ALPHA = 0.55;
const PLAYER_COLOR = 0x4a90e2;

export type CityTabOptions = {
  // Content area size given by GameWindow.
  width: number;
  height: number;
};

export class CityTab extends Container {
  private readonly world = new Container();
  private readonly highlight: Graphics;
  private readonly tileW: number;
  private readonly tileH: number;
  // Center of the content area - the camera keeps the player here.
  private readonly centerX: number;
  private readonly centerY: number;

  // Player position in WORLD coordinates (tween target while walking) and
  // the tile it is standing on / walking to.
  private readonly playerPos = { x: 0, y: 0 };
  private playerCol = GRID_SIZE / 2;
  private playerRow = GRID_SIZE / 2;

  constructor(opts: CityTabOptions) {
    super();
    const { width: w, height: h } = opts;
    this.tileH = TILE_H_FRAC * h;
    this.tileW = this.tileH * 2;
    this.centerX = w / 2;
    this.centerY = h / 2;

    // Crop the map to the paper area (also crops hit-testing).
    const frame = new Graphics().rect(0, 0, w, h).fill(0xffffff);
    this.addChild(frame);
    this.mask = frame;

    this.world.addChild(this.buildGrid());

    // Tap highlight - shown on the walk target, hidden on arrival.
    this.highlight = new Graphics();
    this.diamondPath(this.highlight, 0, 0).fill({ color: HIGHLIGHT_COLOR, alpha: HIGHLIGHT_ALPHA });
    this.highlight.visible = false;
    this.world.addChild(this.highlight);
    this.addChild(this.world);

    // Player marker is NOT part of the world - it is pinned to the view
    // center and the world scrolls underneath it.
    const player = new Graphics()
      .ellipse(0, 0, this.tileH * 0.45, this.tileH * 0.22)
      .fill({ color: 0x000000, alpha: 0.25 })
      .circle(0, -this.tileH * 0.55, this.tileH * 0.42)
      .fill(PLAYER_COLOR);
    player.position.set(this.centerX, this.centerY);
    this.addChild(player);

    const start = this.isoToWorld(this.playerCol, this.playerRow);
    this.playerPos.x = start.x;
    this.playerPos.y = start.y;
    this.updateCamera();

    // The map itself is the input surface; tiles are picked by math (no
    // per-tile interactivity needed).
    this.eventMode = 'static';
    this.hitArea = new Rectangle(0, 0, w, h);
    this.on('pointertap', this.onTap, this);
  }

  // One Graphics for the whole 100x100 board. Choosing paths over per-tile
  // strokes keeps it cheap: two checkerboard fills (one path per color) and
  // the grid as straight cross-lines (an iso grid is just a rotated square
  // grid, so its edges are continuous lines).
  private buildGrid(): Graphics {
    const g = new Graphics();
    for (const parity of [0, 1]) {
      for (let row = 0; row < GRID_SIZE; row++) {
        for (let col = 0; col < GRID_SIZE; col++) {
          if ((col + row) % 2 !== parity) continue;
          const { x, y } = this.isoToWorld(col, row);
          this.diamondPath(g, x, y);
        }
      }
      g.fill(parity === 0 ? TILE_COLOR_A : TILE_COLOR_B);
    }
    for (let i = 0; i <= GRID_SIZE; i++) {
      const a = this.isoToWorld(i - 0.5, -0.5);
      const b = this.isoToWorld(i - 0.5, GRID_SIZE - 0.5);
      g.moveTo(a.x, a.y).lineTo(b.x, b.y);
      const c = this.isoToWorld(-0.5, i - 0.5);
      const d = this.isoToWorld(GRID_SIZE - 0.5, i - 0.5);
      g.moveTo(c.x, c.y).lineTo(d.x, d.y);
    }
    g.stroke({ width: 1, color: TILE_LINE_COLOR });
    return g;
  }

  // Tile grid coords (fractional allowed) -> world-space position of the
  // tile center. The grid is centered around its middle.
  private isoToWorld(col: number, row: number): { x: number; y: number } {
    const c = col - GRID_SIZE / 2;
    const r = row - GRID_SIZE / 2;
    return {
      x: ((c - r) * this.tileW) / 2,
      y: ((c + r) * this.tileH) / 2,
    };
  }

  // Fractional tile coords - used to measure walk distance from a mid-walk
  // position.
  private worldToTileFloat(x: number, y: number): { col: number; row: number } {
    const c = x / (this.tileW / 2);
    const r = y / (this.tileH / 2);
    return {
      col: (c + r) / 2 + GRID_SIZE / 2,
      row: (r - c) / 2 + GRID_SIZE / 2,
    };
  }

  private worldToTile(x: number, y: number): { col: number; row: number } {
    const f = this.worldToTileFloat(x, y);
    return { col: Math.round(f.col), row: Math.round(f.row) };
  }

  private diamondPath(g: Graphics, cx: number, cy: number): Graphics {
    return g.poly([
      cx, cy - this.tileH / 2,
      cx + this.tileW / 2, cy,
      cx, cy + this.tileH / 2,
      cx - this.tileW / 2, cy,
    ]);
  }

  // Keeps the player's world position glued to the view center.
  private updateCamera(): void {
    this.world.position.set(this.centerX - this.playerPos.x, this.centerY - this.playerPos.y);
  }

  private onTap(e: FederatedPointerEvent): void {
    const local = this.world.toLocal(e.global);
    const tile = this.worldToTile(local.x, local.y);
    const col = clamp(tile.col, 0, GRID_SIZE - 1);
    const row = clamp(tile.row, 0, GRID_SIZE - 1);
    if (col === this.playerCol && row === this.playerRow) return;

    const target = this.isoToWorld(col, row);
    this.highlight.position.set(target.x, target.y);
    this.highlight.visible = true;

    // Straight-line walk at constant tile speed. A new tap retargets from
    // the current (mid-walk) position - so the distance must be measured
    // from the ACTUAL position, not from the previous target tile (that
    // would shrink the duration and teleport the player).
    const { col: curCol, row: curRow } = this.worldToTileFloat(this.playerPos.x, this.playerPos.y);
    const distTiles = Math.hypot(col - curCol, row - curRow);
    this.playerCol = col;
    this.playerRow = row;
    gsap.killTweensOf(this.playerPos);
    const tween = gsap.to(this.playerPos, {
      x: target.x,
      y: target.y,
      duration: distTiles / WALK_TILES_PER_SEC,
      ease: 'none',
      onUpdate: () => this.updateCamera(),
      onComplete: () => {
        this.highlight.visible = false;
      },
    });
    // The global gsap timeline is slowed by BATTLE_PACE_SCALE for battle
    // pacing - city movement runs at real speed, so cancel it out here.
    tween.timeScale(BATTLE_PACE_SCALE);
  }
}

function clamp(v: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, v));
}
